'use client';
import { useState } from 'react';

const faqs = [
  {
    question: 'Will this replace me as a coach?',
    answer:
      'No. ElevateX handles the repetitive admin work — onboarding, programming, and weekly check-ins. You still deliver the coaching, build relationships, and make the key decisions. Think of it as your AI backend, not your replacement.',
  },
  {
    question: 'Can I customise the AI to match my coaching style?',
    answer:
      'Absolutely. During your strategy call, we train the AI on your coaching philosophy, tone, and systems. Every output — from onboarding summaries to weekly review drafts — reflects how you coach, not a generic template.',
  },
  {
    question: 'How does the weekly check-in system work?',
    answer:
      'Each week, your clients receive automated check-in reminders. They submit their compliance, sleep, stress, mood, weight, and notes. The AI analyses the data, assigns a RAG status (Green / Amber / Red), and generates a structured review draft for your approval. You review and send.',
  },
  {
    question: 'What if I want to write a programme myself?',
    answer:
      'You always have full control. The AI generates a draft programme based on client data and your philosophy — you review, adjust, and deliver. It removes the blank-page problem, not your coaching input.',
  },
  {
    question: 'How long does setup take?',
    answer:
      'Most coaches are live within a few days after their strategy call. We handle the full technical setup and customisation — you just approve before anything goes live.',
  },
  {
    question: 'Is this built for coaches at a specific scale?',
    answer:
      'ElevateX is designed for online fitness coaches managing 15–40 active clients who are serious about scaling without burning out. If you\'re at that stage and drowning in admin, this is built for you.',
  },
  {
    question: 'What is the 7-day guided pilot?',
    answer:
      'We set up your full ElevateX dashboard, configure all four AI Assistants, and onboard 2–3 of your real clients — all within the first 24 hours. You spend the next 7 days using it with real clients so you can see exactly how it works before committing. No payment required during the pilot.',
  },
  {
    question: 'How many AI Assistants do I get?',
    answer:
      'All four. Every coach gets the full system: AI Client Onboarding (AXIS), AI Programme Builder (FORGE), AI Check-In Reminders (ECHO), and AI Weekly Reviews (NOVA). There\'s no feature gating — you get everything from day one.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section
      id="faq"
      className="py-24 md:py-32 px-5 md:px-8 bg-surface"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="badge badge-coral mb-4">FAQ</span>
          <h2 className="display-md text-foreground mb-5">
            Questions?
            <br />
            <span className="gradient-text">We've got answers.</span>
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs?.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-black/[0.07] overflow-hidden transition-all duration-300 hover:border-primary/30"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 transition-colors"
              >
                <span className="text-lg font-bold text-foreground">{faq?.question}</span>
                <svg
                  className={`w-5 h-5 text-primary shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-muted leading-relaxed">{faq?.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}